import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { activePlatformNotices, type PublicNotice } from '../api/notices.js';
import { useSession } from './session.js';

const DISMISS_KEY = 'cg.platformNotices.dismissed';
const REFRESH_MS = 5 * 60_000;

function readDismissed(): string[] {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const v = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(-50) : [];
  } catch {
    return [];
  }
}

/**
 * 平台公告(维护 / 政策更新):各端顶部横幅与消息中心置顶(原型 global/errors「维护公告提前 24 小时在各端消息中心与官网发布」)。
 * 打开应用拉一次、之后 5 分钟刷新;横幅可关(只记在本机浏览器),维护进行中的那条不能关。
 */
export const usePlatformNotices = defineStore('platformNotices', () => {
  const items = ref<PublicNotice[]>([]);
  const dismissed = ref<string[]>(readDismissed());
  const now = ref(Date.now());
  let timer: ReturnType<typeof setInterval> | null = null;
  let loaded = false;

  async function refresh() {
    try {
      items.value = (await activePlatformNotices(useSession().api)).items;
    } catch {
      /* 拿不到就不显示 */
    }
    now.value = Date.now();
  }
  function start() {
    if (loaded) return;
    loaded = true;
    void refresh();
    timer = setInterval(() => void refresh(), REFRESH_MS);
  }
  function stop() {
    if (timer) clearInterval(timer);
    timer = null;
    loaded = false;
  }

  /** 维护进行中(当前时间在维护时间窗里) */
  const inWindow = (n: PublicNotice) =>
    n.category === 'maintenance' &&
    !!n.windowStart &&
    !!n.windowEnd &&
    new Date(n.windowStart).getTime() <= now.value &&
    new Date(n.windowEnd).getTime() > now.value;
  const banners = computed(() => items.value.filter((n) => inWindow(n) || !dismissed.value.includes(n.id)));

  function dismiss(id: string) {
    dismissed.value = [...dismissed.value.filter((x) => x !== id), id].slice(-50);
    try {
      localStorage.setItem(DISMISS_KEY, JSON.stringify(dismissed.value));
    } catch {
      /* 隐私模式等写不进去就只在本次会话里关 */
    }
  }

  return { items, banners, inWindow, start, stop, refresh, dismiss };
});
